import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import CharacterGuide from "@/components/feature/CharacterGuide";

const sections = [
  {
    key: "plan",
    no: "01",
    title: "基本プランの容量",
    desc: "契約している容量と、実際に使っているデータ量を比較します。使い方に対して容量が多すぎないか確認しましょう。",
    fields: [
      { name: "planName", label: "契約プラン名", placeholder: "例：ギガライト" },
      { name: "planFee", label: "月額（税込）", placeholder: "例：4,565", suffix: "円" },
      { name: "planLimit", label: "契約容量", placeholder: "例：20", suffix: "GB" },
      { name: "usedData", label: "先月の使用量", placeholder: "例：6", suffix: "GB" },
    ],
  },
  {
    key: "option",
    no: "02",
    title: "有料オプション",
    desc: "契約時に付いた留守電、故障保証、動画サービスなどをすべて書き出します。使っていないものはこの機会に整理を。",
    fields: [
      { name: "optionList", label: "オプション名（改行で複数可）", textarea: true, placeholder: "例：\n故障保証 660円\n留守番電話 330円\n動画見放題 550円" },
      { name: "optionFee", label: "オプション合計（税込）", placeholder: "例：1,540", suffix: "円" },
    ],
  },
  {
    key: "device",
    no: "03",
    title: "端末代金の残債",
    desc: "分割払いの残り回数と、返却プログラムの条件を確認します。買い切りと返却で総支払額が大きく変わります。",
    fields: [
      { name: "deviceName", label: "端末名", placeholder: "例：iPhone 15 128GB" },
      { name: "devicePay", label: "月々の端末代金", placeholder: "例：2,530", suffix: "円" },
      { name: "deviceMonthsLeft", label: "残り回数", placeholder: "例：18", suffix: "回" },
      { name: "returnProgram", label: "返却プログラム利用", placeholder: "はい / いいえ" },
    ],
  },
  {
    key: "call",
    no: "04",
    title: "通話料金の内訳",
    desc: "定額オプションと従量課金分を分けます。5分以内が多いか、長電話が多いかで最適な選び方が変わります。",
    fields: [
      { name: "callOption", label: "通話定額オプション", placeholder: "例：5分定額 880円" },
      { name: "callExtra", label: "定額外の通話料", placeholder: "例：420", suffix: "円" },
      { name: "callMinutes", label: "月の通話時間の目安", placeholder: "例：30", suffix: "分" },
    ],
  },
] as const;

type Values = Record<string, string>;

export default function BillCheck() {
  const [values, setValues] = useState<Values>({});
  const [showResult, setShowResult] = useState(false);

  const summary = useMemo(() => {
    const plan = parseInt(values.planFee || "0", 10) || 0;
    const opt = parseInt(values.optionFee || "0", 10) || 0;
    const device = parseInt(values.devicePay || "0", 10) || 0;
    const call = parseInt(values.callExtra || "0", 10) || 0;
    const total = plan + opt + device + call;

    const notes: string[] = [];
    const contractLimit = parseInt(values.planLimit || "0", 10);
    const used = parseInt(values.usedData || "0", 10);
    if (contractLimit && used && used < contractLimit / 3) {
      notes.push("契約容量に対して使用量が少なめです。1段階小さいプランで月1,000〜2,000円下がる可能性があります。");
    }
    if (opt >= 1000) {
      notes.push("オプション合計が1,000円を超えています。使用頻度の低いものから解約を検討しましょう。");
    }
    if (values.returnProgram?.includes("はい")) {
      notes.push("返却プログラム利用中は、返却時の査定条件と途中解約時のペナルティを必ず確認してください。");
    }
    if ((parseInt(values.callMinutes || "0", 10) || 0) < 10 && values.callOption) {
      notes.push("通話時間が短いため、通話定額オプションは不要な可能性があります。");
    }

    return { plan, opt, device, call, total, notes };
  }, [values]);

  const update = (k: string, v: string) => setValues({ ...values, [k]: v });

  return (
    <Layout>
      <section className="border-b border-background-300 bg-background-200">
        <div className="mx-auto max-w-[900px] px-4 md:px-6 py-10 md:py-14">
          <div className="text-xs text-secondary-600 font-semibold tracking-widest">請求書チェックツール</div>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-900">請求書の「4つの項目」を整理する</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7">
            通信会社を変えなくても、この4項目を書き出すだけで見直しポイントが分かります。
            入力内容は端末内で確認するだけで、外部には送信されません。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[900px] px-4 md:px-6 py-10 md:py-14 space-y-6">
        <CharacterGuide
          size="md"
          message="明細を見ながら、分かるところだけ入力してください。全部埋めなくても大丈夫です。"
        />
        {sections.map((s) => (
          <div key={s.key} className="rounded-xl border border-background-300 bg-background-50 p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="text-primary-500 font-bold">{s.no}</div>
              <div className="flex-1">
                <h2 className="text-lg md:text-xl font-bold text-foreground-900">{s.title}</h2>
                <p className="mt-1 text-[13px] md:text-sm text-foreground-600 leading-6">{s.desc}</p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {s.fields.map((f) => (
                <div key={f.name} className={"textarea" in f && f.textarea ? "md:col-span-2" : ""}>
                  <label className="block text-xs font-semibold text-foreground-700 mb-1.5">{f.label}</label>
                  {"textarea" in f && f.textarea ? (
                    <textarea
                      value={values[f.name] || ""}
                      onChange={(e) => update(f.name, e.target.value)}
                      placeholder={f.placeholder}
                      rows={4}
                      className="w-full text-sm px-3 py-2 rounded-md border border-background-300 bg-background-50 focus:border-primary-500 focus:outline-none"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        value={values[f.name] || ""}
                        onChange={(e) => update(f.name, e.target.value)}
                        placeholder={f.placeholder}
                        className="flex-1 text-sm px-3 py-2 rounded-md border border-background-300 bg-background-50 focus:border-primary-500 focus:outline-none"
                      />
                      {"suffix" in f && f.suffix && <div className="text-xs text-foreground-500">{f.suffix}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="text-xs text-foreground-500">※ 入力内容はブラウザ上でのみ使用され、外部には送信されません。</div>
          <button
            onClick={() => setShowResult(true)}
            className="px-6 py-3 rounded-md bg-primary-500 text-white font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            見直しポイントを表示
          </button>
        </div>

        {showResult && (
          <div className="rounded-xl border border-background-300 bg-background-50 p-6 md:p-8">
            <div className="flex items-center gap-2 text-primary-500">
              <i className="ri-file-list-3-line text-xl"></i>
              <div className="text-sm font-semibold">整理結果</div>
            </div>
            <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-900">4項目の合計と見直しポイント</h2>

            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div className="p-4 bg-background-100 rounded-md"><div className="text-[11px] text-foreground-500">基本プラン</div><div className="mt-1 font-bold text-foreground-900">{summary.plan.toLocaleString()}円</div></div>
              <div className="p-4 bg-background-100 rounded-md"><div className="text-[11px] text-foreground-500">オプション</div><div className="mt-1 font-bold text-foreground-900">{summary.opt.toLocaleString()}円</div></div>
              <div className="p-4 bg-background-100 rounded-md"><div className="text-[11px] text-foreground-500">端末代金</div><div className="mt-1 font-bold text-foreground-900">{summary.device.toLocaleString()}円</div></div>
              <div className="p-4 bg-background-100 rounded-md"><div className="text-[11px] text-foreground-500">通話料金</div><div className="mt-1 font-bold text-foreground-900">{summary.call.toLocaleString()}円</div></div>
              <div className="p-4 bg-primary-50 border border-primary-100 rounded-md"><div className="text-[11px] text-primary-700">月額合計</div><div className="mt-1 font-bold text-primary-700">{summary.total.toLocaleString()}円</div></div>
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold text-foreground-900 mb-2">見直しのポイント</div>
              {summary.notes.length === 0 ? (
                <div className="text-sm text-foreground-600">入力内容からは大きな見直し候補は見つかりませんでした。他社との比較で改善余地を探すのがおすすめです。</div>
              ) : (
                <ul className="space-y-2 text-sm text-foreground-700">
                  {summary.notes.map((n, i) => (
                    <li key={i} className="flex items-start gap-2"><i className="ri-lightbulb-line text-accent-500 mt-0.5"></i>{n}</li>
                  ))}
                </ul>
              )}
            </div>

            <CharacterGuide
              className="mt-6"
              size="sm"
              message="次に確認することは、この中から気になった項目を1つ決めて、公式サイトや明細で確かめるだけ。あせって全部を変える必要はありません。"
            />

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/diagnosis" className="px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap text-center">
                詳しい確認手順を見る（料金診断）
              </Link>
              <Link to="/compare" className="px-5 py-2.5 rounded-md bg-background-50 border border-background-400 text-foreground-900 text-sm font-semibold hover:border-primary-500 cursor-pointer whitespace-nowrap text-center">
                条件をそろえて比較する
              </Link>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
